import { z } from 'zod';
import { evaluateExpression, validateCSVFormat, parseCSVLine } from '../shared';

/**
 * Base schema for individual field validation
 * Keep ALL input values as strings (since inputs emit strings),
 * and let Zod coerce/validate.
 */
export const baseSchema = z.object({
  project: z.string().min(1, 'Project is required'),
  title: z.string().min(1, 'Title is required'),

  length: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0;
  }, 'Length must be a valid expression greater than 0'),

  tributaryWidth: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0;
  }, 'Tributary Width must be a valid expression greater than 0'),

  addedWeight: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Added Weight must be a valid expression greater than or equal to 0'),

  // Dropdown fields
  supports: z.coerce
    .number()
    .int()
    .refine(n => [1, 2, 3, 4, 5, 6].includes(n), 'Supports must be between 1 and 6'),

  orientation: z.coerce
    .number()
    .int()
    .refine(n => [0, 1].includes(n), 'Orientation must be 1 (Horizontal) or 0 (Vertical)'),
  sectionType: z.string().min(1, 'Section type is required'),
  sectionName: z.string().min(1, 'Section name is required'),
  direction: z.coerce
    .number()
    .int()
    .refine(n => [0, 1].includes(n), 'Direction must be 1 (Minor) or 0 (Major)'),
  restrained: z.coerce
    .number()
    .int()
    .refine(n => [0, 1].includes(n), 'Restrained must be 1 (Restrained) or 0 (Unrestrained)'),

  fdy: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 100000;
  }, 'Fdy must be a valid expression between 1 and 100000'),

  fdu: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 100000;
  }, 'Fdu must be a valid expression between 1 and 100000'),

  topFlange: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Top Flange must be a valid expression greater than or equal to 0'),

  bottomFlange: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Bottom Flange must be a valid expression greater than or equal to 0'),

  doublePlate: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Double Plate must be a valid expression greater than or equal to 0'),

  axialLoad: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Axial Load must be a valid expression greater than or equal to 0'),

  pressureHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  timeHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  u0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null;
  }, 'u0 must be a valid expression'),

  v0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null;
  }, 'v0 must be a valid expression')
});

/**
 * Full schema with cross-field validation
 */
export const schema = baseSchema.superRefine((data, ctx) => {
  // 1. Flange Logic
  if (data.restrained === 1) {
    if (evaluateExpression(data.topFlange) !== null && evaluateExpression(data.length) !== null && evaluateExpression(data.topFlange)! > evaluateExpression(data.length)!) {
      ctx.addIssue({
        code: 'custom',
        message: 'Top Flange cannot exceed Length',
        path: ['topFlange']
      });
    }

    if (evaluateExpression(data.bottomFlange) !== null && evaluateExpression(data.length) !== null && evaluateExpression(data.bottomFlange)! > evaluateExpression(data.length)!) {
      ctx.addIssue({
        code: 'custom',
        message: 'Bottom Flange cannot exceed Length',
        path: ['bottomFlange']
      });
    }
  }

  // 2. CSV Logic - ensure pressure and time histories have same length
  const pLen = parseCSVLine(data.pressureHistory)?.length;
  const tLen = parseCSVLine(data.timeHistory)?.length;
  if (pLen !== tLen) {
    const msg = 'Pressure and time histories must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['pressureHistory'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['timeHistory'] });
  }
});

/**
 * Helper types
 */
export type FieldName = keyof z.infer<typeof baseSchema>;
export type FormValues = z.input<typeof schema>; // raw form shape (strings from inputs/selects)
export type ParsedValues = z.output<typeof schema>; // parsed shape (numbers after coercion)
export type FormErrors = Partial<Record<FieldName, string | null>>;

/**
 * Validate a single field
 */
export function validateOneField<K extends FieldName>(key: K, raw: unknown): string | null {
  const fieldSchema = baseSchema.shape[key];
  const res = fieldSchema.safeParse(raw);
  return res.success ? null : (res.error.issues[0]?.message ?? 'Invalid value');
}
